/* eslint-disable import/named */
import React, { useEffect, useState } from 'react';

import { makeStyles, Container, Grid, Typography } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

import { DEFAULT_VIDEO_CRITERIA, DEFAULT_DOCS_CRITERIA, DEFAULT_STUDIES_CRITERIA } from '../../blocs/selfHelpMaterials.bloc';
import ContentFilters from '../../components/selfHelpMaterials/ContentFilters';
import DocumentList from '../../components/selfHelpMaterials/DocumentList';
import PremiumSubsription from '../../components/selfHelpMaterials/PremiumSubsription';
import SelfHelpTour from '../../components/selfHelpMaterials/SelfHelpTour';
import StudyList from '../../components/selfHelpMaterials/StudyList';
import VideoList from '../../components/selfHelpMaterials/VideoList';
import SimpleTooltip from '../../components/simpleTooltip/SimpleTooltip';
import useBlocRefHook from '../../utils/useBlocRef.hook';

const useStyles = makeStyles((theme) => {
  return {
    documentListContainer: {
      marginTop: '20px',
    },
    documentSectionContainer: {
      marginTop: '40px',
    },
    filtersContainer: {
      marginTop: '24px',
    },
    listParentClass: {
      marginBottom: '4px',
      marginTop: '4px',
      paddingLeft: '20px',
    },
    noResultsContainer: {
      margin: '48px 0px',
    },
    parentContainer: {
      marginBottom: '50px',
      marginTop: '40px',
    },
    premiumContentContainer: {
      marginTop: '40px',
    },
    responsiveFullWidth: {
      width: '100%',
      [theme.breakpoints.up('md')]: {
        width: 'auto',
      },
    },
    typographySectionTitle: {
      color: '#172B4D',
      fontSize: '20px',
      fontWeight: 'bold',
      lineHeight: '25px',
      marginBottom: '0px',
      marginRight: '8px',
    },
    typographySectionTitleContainer: {
      alignItems: 'center',
      display: 'flex',
    },
    typographySubtitle: {
      color: '#999999',
      fontSize: '12px',
      lineHeight: '20px',
      marginBottom: '0px',
      maxWidth: '800px',
    },
    typographyTitle: {
      color: '#172B4D',
      fontSize: '24px',
      fontWeight: '600',
      lineHeight: '29px',
      marginBottom: '0px',
    },
    typographyTooltip: {
      marginBottom: '0px',
      marginTop: '4px',
    },
    videoListContainer: {
      marginTop: '20px',
    },
    videoSectionContainer: {
      marginTop: '40px',
    },
  };
});

export default () => {
  const classes = useStyles();
  const history = useHistory();

  const [videos, setVideos] = useState([]);
  const [videosTotal, setVideosTotal] = useState(0);
  const [videosCriteria, setVideosCriteria] = useState(DEFAULT_VIDEO_CRITERIA);

  const [documents, setDocuments] = useState([]);
  const [documentsTotal, setDocumentsTotal] = useState(0);
  const [documentsCriteria, setDocumentsCriteria] = useState(DEFAULT_DOCS_CRITERIA);

  const [studies, setStudies] = useState([]);
  const [studiesTotal, setStudiesTotal] = useState(0);
  const [studiesCriteria, setStudiesCriteria] = useState(DEFAULT_STUDIES_CRITERIA);

  const [runJoyride, setRunJoyride] = useState(false);
  const [categoryList, setCategoryList] = useState([]);

  const blocRef = useBlocRefHook('selfHelpMaterialsBloc');

  useEffect(() => {
    blocRef.current.getCurrentUser();

    const documentsSub = blocRef.current.documents$.subscribe(setDocuments);
    const documentsTotalSub = blocRef.current.documentsCount$.subscribe(setDocumentsTotal);
    const documentsCriteriaSub = blocRef.current.documentCriteria$.subscribe(setDocumentsCriteria);

    const videosSub = blocRef.current.videos$.subscribe(setVideos);
    const videosTotalSub = blocRef.current.videosCount$.subscribe(setVideosTotal);
    const videosCriteriaSub = blocRef.current.videoCriteria$.subscribe(setVideosCriteria);

    const studiesSub = blocRef.current.studies$.subscribe(setStudies);
    const studiesTotalSub = blocRef.current.studiesCount$.subscribe(setStudiesTotal);
    const studiesCriteriaSub = blocRef.current.studyCriteria$.subscribe(setStudiesCriteria);

    const runJoyrideSub = blocRef.current.runJoyride$.subscribe(setRunJoyride);
    const categoryListSub = blocRef.current.selfHelpCategories$.subscribe(setCategoryList);

    return () => {
      documentsSub.unsubscribe();
      documentsTotalSub.unsubscribe();
      documentsCriteriaSub.unsubscribe();
      videosSub.unsubscribe();
      videosTotalSub.unsubscribe();
      videosCriteriaSub.unsubscribe();
      studiesSub.unsubscribe();
      studiesTotalSub.unsubscribe();
      studiesCriteriaSub.unsubscribe();
      runJoyrideSub.unsubscribe();
      categoryListSub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscribeClick = () => {
    blocRef.current.openSubscribeDialog();
  };

  const handleSearchChange = (data) => {
    const criteria = { page: 0 };
    if (data.keyword || data.keyword === '') criteria.keyword = data.keyword;
    if (data.selfHelpCategoryId || data.selfHelpCategoryId === '') criteria.selfHelpCategoryId = data.selfHelpCategoryId;

    blocRef.current.updateVideoCriteria(criteria);
    blocRef.current.updateDocumentCriteria(criteria);
    blocRef.current.updateStudyCriteria(criteria);
  };

  const handleVideoPageUpdate = (page) => {
    blocRef.current.updateVideoCriteria({ page });
  };

  const handleDocumentPageUpdate = (page) => {
    blocRef.current.updateDocumentCriteria({ page });
  };

  const handleStudyPageUpdate = (page) => {
    blocRef.current.updateStudyCriteria({ page });
  };

  const handleOpenViewer = (data) => {
    blocRef.current.openViewer(data);
  };

  const handleCloseJoyride = () => {
    blocRef.current.closeJoyride();
  };

  const handleOpenPatientDashboard = () => {
    history.push('/patient-dashboard');
  };

  return (
    <div className={classes.parentContainer}>
      <SelfHelpTour runJoyride={runJoyride} onOpenPatientDashboard={handleOpenPatientDashboard} onCloseJoyride={handleCloseJoyride} />

      <Container maxWidth="lg">
        {/* HEADER */}
        <Grid container justify="space-between" alignItems="flex-end">
          <Grid item xs={12}>
            <Typography className={`${classes.typographyTitle} text-left`}>Self-Help Materials</Typography>
            <Typography className={`${classes.typographySubtitle} text-left`}>
              Feel free to browse our free selection of self-help materials. You will find information about common conditions and radiology studies as well as have access to
              comparison studies that you can use to compare and contrast with your own.
            </Typography>
          </Grid>
        </Grid>

        {/* PREMIUM CONTENT */}
        <div className={classes.premiumContentContainer}>
          <PremiumSubsription onSubsribeClick={handleSubscribeClick} />
        </div>

        {/* Filters */}
        <div className={classes.filtersContainer}>
          <ContentFilters onSearchChange={handleSearchChange} categoryList={categoryList} />
        </div>

        {/* VIDEO LIST */}
        <div className={classes.videoSectionContainer}>
          <div className={classes.typographySectionTitleContainer}>
            <Typography className={`${classes.typographySectionTitle} text-left`}>Videos</Typography>
            <SimpleTooltip
              wide
              title={
                <>
                  <Typography className={classes.typographyTooltip} variant="caption">
                    This section contains multiple types of videos which:
                  </Typography>
                  <ul className={classes.listParentClass}>
                    <li>
                      <Typography className={classes.typographyTooltip} variant="caption">
                        Demonstrate normal anatomy on MRI and CT scans
                      </Typography>
                    </li>
                    <li>
                      <Typography className={classes.typographyTooltip} variant="caption">
                        Demonstrate common illnesses and injuries on MRI and CT scans (ex. Multiple Sclerosis, ACL tear, Herniated Disk etc.)
                      </Typography>
                    </li>
                    <li>
                      <Typography className={classes.typographyTooltip} variant="caption">
                        Explain how to look at an MRI or CT scan
                      </Typography>
                    </li>
                    <li>
                      <Typography className={classes.typographyTooltip} variant="caption">
                        Explain common radiology procedures
                      </Typography>
                    </li>
                  </ul>
                  <Typography className={classes.typographyTooltip} variant="caption">
                    *Note: A free account only contains a few sample videos.
                  </Typography>
                </>
              }
            />
          </div>
          <div className={classes.videoListContainer}>
            <VideoList videos={videos} total={videosTotal} onOpenViewer={handleOpenViewer} criteria={videosCriteria} onPageUpdate={handleVideoPageUpdate} />
          </div>
        </div>

        {/* DOCUMENT LIST */}
        <div className={classes.documentSectionContainer}>
          <div className={classes.typographySectionTitleContainer}>
            <Typography className={`${classes.typographySectionTitle} text-left`}>Articles</Typography>
            <SimpleTooltip
              wide
              title={
                <Typography className={classes.typographyTooltip} variant="caption">
                  This section contains short, informative articles written by our expert radiologists which explain common conditions and procedures from an imaging perspective.
                </Typography>
              }
            />
          </div>
          <div className={classes.documentListContainer}>
            <DocumentList documents={documents} total={documentsTotal} onOpenViewer={handleOpenViewer} criteria={documentsCriteria} onPageUpdate={handleDocumentPageUpdate} />
          </div>
        </div>

        {/* STUDIES LIST */}
        <div className={classes.documentSectionContainer}>
          <div className={classes.typographySectionTitleContainer}>
            <Typography className={`${classes.typographySectionTitle} text-left`}>Normal Comparison Studies</Typography>
            <SimpleTooltip
              wide
              title={
                <Typography className={classes.typographyTooltip} variant="caption">
                  This section contains normal radiology scans (MRI, CT) that you can use to compare to your own scan.
                </Typography>
              }
            />
          </div>
          <div className={classes.documentListContainer}>
            <StudyList studies={studies} total={studiesTotal} onOpenViewer={handleOpenViewer} criteria={studiesCriteria} onPageUpdate={handleStudyPageUpdate} />
          </div>
        </div>
      </Container>
    </div>
  );
};
